import { X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import type { Plan, RunState } from '../v4/contracts'
import { stepAction, stepKind } from '../v4/step-label'
import { planCardOf } from '../v4/PlanCard'
import { runStepStatus } from './runGraph'
import { stepFailure } from './runStory'
import { valueText } from './evidenceText'
import StepEditor from './StepEditor'

const LABEL = 'text-sm font-medium text-foreground'
const BOX = 'rounded-lg border border-input bg-background p-3 text-sm leading-relaxed text-foreground dark:bg-dark-card'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className={LABEL}>{title}</span>
      {children}
    </div>
  )
}

/**
 * The docked panel for a plan step the old builder's node opens (the twin of the v1 node
 * properties): what the step does in the engine's words, its kind and status, the plan card's
 * facts and warning when the plan carries one, and the evidence it produced with its coverage.
 * While the plan awaits approval the step can be edited (StepEditor); once approved, the plan is
 * the engine's. The step's raw tool operation, bindings, and model and tool calls are the engine's
 * internals and are not shown.
 */
export default function StepInspector({
  run,
  plan,
  stepId,
  onClose,
  editable = false,
  busy = false,
  error,
  onSave,
}: {
  run: RunState
  plan: Plan
  stepId: string
  onClose: () => void
  /** The plan awaits approval: the step can be edited (StepEditor) before it. */
  editable?: boolean
  busy?: boolean
  /** The engine's refusal of the last edit, for the editor. */
  error?: string
  onSave?: (plan: Plan) => Promise<boolean>
}) {
  const { t } = useTranslation()
  const step = plan.steps.find((s) => s.id === stepId)
  if (!step) return null
  // A step a new version kept has its trace on the version it ran in.
  const traced = run.trace.steps.filter((x) => x.stepId === stepId).at(-1)
  const evidence = run.trace.evidence.filter((e) => traced?.evidenceIds?.includes(e.id))
  const card = planCardOf(run.events, plan.version)?.steps.find((s) => s.id === stepId) ?? null
  const coverage = evidence.filter((e) => e.coverage)
  const status = runStepStatus(run, plan, step)
  const failure = status === 'failed' || status === 'waiting_for_user' ? stepFailure(run, stepId) : null

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3.5">
        <h2 className="min-w-0 truncate text-sm font-semibold text-foreground" title={step.title}>
          {step.title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common:close', { defaultValue: 'Close' })}
          className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden p-4 [&>*]:shrink-0">
        <Section title={t('workflow:node.what', { defaultValue: 'What it does' })}>
          <div className={BOX}>
            {card?.text ?? stepAction(step)}
            {card?.warning && <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">{card.warning}</p>}
          </div>
        </Section>
        <div className="grid grid-cols-2 gap-3">
          <Section title={t('workflow:node.kind', { defaultValue: 'Kind' })}>
            <div className={BOX}>{stepKind(step)}</div>
          </Section>
          <Section title={t('workflow:node.status', { defaultValue: 'Status' })}>
            <div className={BOX}>
              {status.replace(/_/g, ' ')}
              {failure && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{failure}</p>}
            </div>
          </Section>
        </div>
        {editable && onSave && (
          <Section title={t('workflow:node.edit_title', { defaultValue: 'Edit this step' })}>
            <StepEditor key={`${plan.version}:${step.id}`} plan={plan} step={step} busy={busy} error={error} onSave={onSave} />
          </Section>
        )}
        {card && card.facts.length > 0 && (
          <Section title={t('workflow:node.facts', { defaultValue: 'Facts' })}>
            <dl className={`${BOX} grid grid-cols-[auto_1fr] gap-x-3 gap-y-1`}>
              {card.facts.map((f, i) => (
                <div key={i} className="contents">
                  <dt className="text-muted-foreground">{f.label}</dt>
                  <dd>{f.text}</dd>
                </div>
              ))}
            </dl>
          </Section>
        )}
        {card?.method && (
          <Section title={t('workflow:node.method_step', { defaultValue: 'Method step' })}>
            <div className={BOX}>
              <span className="text-muted-foreground">{card.method.document} · {card.method.ref}</span>
              <p className="mt-1 italic">“{card.method.quote}”</p>
            </div>
          </Section>
        )}
        <Section title={t('workflow:node.evidence', { defaultValue: 'Evidence' })}>
          <div className={BOX}>
            {evidence.length === 0 ? (
              <span className="text-muted-foreground">
                {traced
                  ? t('workflow:node.no_evidence', { defaultValue: 'No evidence from this step' })
                  : t('workflow:node.not_started', { defaultValue: 'Workflow not started yet' })}
              </span>
            ) : (
              <ul className="space-y-1">
                {evidence.map((e) => (
                  <li key={e.id}>
                    <span className="font-mono text-xs text-muted-foreground">{e.id}</span>{' '}
                    {e.subject.display ?? e.subject.id}: {valueText(e, e.outcome)}
                    {e.at ? ` at ${e.at.slice(0, 16)}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Section>
        {coverage.length > 0 && (
          <Section title={t('workflow:node.coverage', { defaultValue: 'Coverage' })}>
            <div className={BOX}>
              {coverage.map((e) => `${e.coverage!.observed} of ${e.coverage!.expected} samples`).join('; ')}
            </div>
          </Section>
        )}
      </div>
    </div>
  )
}
